import { Formik, FormikHelpers, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { SafeAreaView } from 'react-native-safe-area-context';

import {
  Button,
  EmailInput,
  NameInput,
  PhoneNumberMaskInput,
  Text,
  createMqStyles,
  useMqSelect,
} from '@fhs/ui';
import {
  formatPhoneNumber,
  isDayValid,
  isEmailValid,
  isMonthValid,
  isNameValid,
  isPhoneValid,
  isYearValid,
  stripPhoneNumber,
} from '@fhs/ui/src/components/form-text-inputs/masks';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { useAuthRouteBase } from '../auth-route-base-context';
import {
  CheckboxInputFormik,
  DateOfBirthInputFormik,
  EmailInUseActionSheet,
  FirehouseInsidersLabel,
  PromotionalEmailsLabel,
  SingleInputFormik,
  TermsOfServiceLabel,
} from '../components';
import { useJwtIdentifier, useNavigateWithJwt, useSignUp } from '../hooks';
import { SignUpError, SignUpInput, SignUpInputErrors } from '../types';

const ServerError = ({
  signUpError,
  clearError,
}: {
  signUpError: SignUpError | null;
  clearError: (e: SignUpError | null) => void;
}) => {
  const { setFieldError } = useFormikContext();
  useEffect(() => {
    if (!signUpError) {
      return;
    }
    switch (signUpError) {
      case SignUpError.USER_INVALID_AGE:
        setFieldError('dob.genericError', 'Your age is not within our allowed range');
        // After consuming server error, clear it to re-render the error in the future
        clearError(null);
        break;
      default:
        break;
    }
  }, [signUpError, setFieldError, clearError]);
  return null;
};

export function SignUp() {
  const { paths } = useAuthRouteBase();
  const styles = useMqStyles();
  const isMobile = useMqSelect({ $ltDesktop: true }, false);
  const { getJwtIdentifier } = useJwtIdentifier();
  const { signUp, signUpError, setSignUpError } = useSignUp();
  const { navigateWithJWT } = useNavigateWithJwt();
  const { validatedIdentifiers } = getJwtIdentifier();
  const firehouseInsidersEnabled = useFlag('enable-firehouse-insiders');

  const attemptSignUp = useCallback(
    async (values: SignUpInput, { setSubmitting }: FormikHelpers<SignUpInput>) => {
      setSubmitting(true);
      const {
        name,
        email,
        phoneNumber,
        dob,
        wantsPromotionalEmail,
        firehouseSubsInsidersProgramOptIn,
      } = values;
      try {
        await signUp({
          name,
          email,
          phoneNumber,
          dob,
          wantsPromotionalEmail,
          // Forward only when the Insiders checkbox is rendered; otherwise leave undefined
          // so the server treats it as "not specified" and skips writing the field.
          firehouseSubsInsidersProgramOptIn: firehouseInsidersEnabled
            ? firehouseSubsInsidersProgramOptIn
            : undefined,
        });
      } catch (e) {
      } finally {
        setSubmitting(false);
      }
    },
    [signUp, firehouseInsidersEnabled]
  );

  const validateForm = useCallback((values: SignUpInput) => {
    const errors: SignUpInputErrors = {};
    if (!values.name) {
      errors.name = 'Name is required';
    }
    if (values.name && !isNameValid(values.name)) {
      errors.name = 'Please enter a valid name';
    }
    if (!values.email) {
      errors.email = 'Email is required';
    }
    if (values.email && !isEmailValid(values.email)) {
      errors.email = 'Please enter a valid email';
    }
    const phoneDigits = stripPhoneNumber(values.phoneNumber);
    if (phoneDigits.length === 0) {
      errors.phoneNumber = 'Phone Number is required';
    } else if (!isPhoneValid(values.phoneNumber)) {
      errors.phoneNumber = 'Please enter a valid phone number';
    }
    const { dobMonth = '', dobDay = '', dobYear = '' } = values.dob || {};
    errors.dob = {};
    const isDobOptional =
      (dobMonth === '' && dobDay === '' && dobYear === '') ||
      (isMonthValid(dobMonth) &&
        isDayValid(dobDay) &&
        isYearValid(dobYear) &&
        dobMonth !== '' &&
        dobDay !== '' &&
        dobYear !== '');
    if (!isDobOptional && !isMonthValid(dobMonth)) {
      errors.dob.dobMonth = 'Please enter a valid month (MM)';
    }
    if (!isDobOptional && !isDayValid(dobDay)) {
      errors.dob.dobDay = 'Please enter a valid day (DD)';
    }
    if (!isDobOptional && !isYearValid(dobYear)) {
      errors.dob.dobYear = 'Please enter a valid year (YYYY)';
    }
    if (isDobOptional) {
      delete errors.dob;
    }
    if (!values.agreesToTermsOfService) {
      errors.agreesToTermsOfService = true;
    }
    return errors;
  }, []);
  const initialValues: SignUpInput = useMemo(
    () => ({
      name: '',
      email: validatedIdentifiers.emailAddress ?? '',
      phoneNumber: formatPhoneNumber(validatedIdentifiers.phoneNumber),
      dob: {
        dobMonth: '',
        dobDay: '',
        dobYear: '',
      },
      wantsPromotionalEmail: false,
      firehouseSubsInsidersProgramOptIn: false,
      agreesToTermsOfService: false,
    }),
    [validatedIdentifiers]
  );
  const onEmaiSignIn = useCallback(() => {
    navigateWithJWT(paths.continueWithEmail);
  }, [navigateWithJWT, paths.continueWithEmail]);
  return (
    <Formik validate={validateForm} initialValues={initialValues} onSubmit={attemptSignUp}>
      {({ isSubmitting, submitForm, values, setFieldError }) => (
        <>
          <ServerError signUpError={signUpError} clearError={setSignUpError} />
          <KeyboardAwareScrollView>
            <SafeAreaView edges={['bottom']} style={{ flex: 1 }}>
              <ScrollView contentContainerStyle={{ gap: 24 }}>
                {!isMobile && <Text.Heading type={'three'}>Create an Account </Text.Heading>}
                <View>
                  <SingleInputFormik label="Name" name="name" component={NameInput} required />
                </View>
                <View>
                  <SingleInputFormik
                    label="Email Address"
                    name="email"
                    component={EmailInput}
                    readOnly={!!validatedIdentifiers.emailAddress}
                    required
                  />
                </View>
                <View>
                  <SingleInputFormik
                    label="Phone Number"
                    name="phoneNumber"
                    component={PhoneNumberMaskInput}
                    readOnly={!!validatedIdentifiers.phoneNumber}
                    required
                  />
                </View>
                <View style={styles.dobContainer}>
                  <DateOfBirthInputFormik />
                </View>
                <CheckboxInputFormik
                  name="wantsPromotionalEmail"
                  component={PromotionalEmailsLabel}
                  label="I want to receive special offers and other information from Firehouse Subs®"
                />
                {firehouseInsidersEnabled && (
                  <CheckboxInputFormik
                    name="firehouseSubsInsidersProgramOptIn"
                    component={FirehouseInsidersLabel}
                    label="I want to join Firehouse Subs Insiders, consent to receiving occasional invites to surveys or focus groups via email, and acknowledge that I have read and agree to the Firehouse Subs Insiders Terms"
                  />
                )}
                <CheckboxInputFormik
                  name="agreesToTermsOfService"
                  component={TermsOfServiceLabel}
                  label="I agree to the Terms of Service, Privacy Policy and Firehouse Rewards Terms"
                />
                <View style={styles.continueButton}>
                  <Button onPress={submitForm} loading={isSubmitting} disabled={isSubmitting}>
                    <Button.Text>Create Account</Button.Text>
                  </Button>
                </View>
              </ScrollView>
            </SafeAreaView>
          </KeyboardAwareScrollView>
          {signUpError && (
            <EmailInUseActionSheet
              isVisible={signUpError === SignUpError.USER_ALREADY_EXISTS}
              email={values.email}
              onSignIn={onEmaiSignIn}
              onUseDifferentEmail={() => {
                setSignUpError(null);
                setFieldError('email', 'Please enter a different email');
              }}
            />
          )}
        </>
      )}
    </Formik>
  );
}

const useMqStyles = createMqStyles({
  dateContainer: {
    $base: {
      marginVertical: 6,
      gap: 12,
      width: '100%',
      flex: 0,
    },
  },
  dobContainer: {
    $base: {
      marginVertical: 12,
      width: '100%',
    },
  },
  dobText: {
    $base: {
      textAlign: 'center',
      width: '100%',
    },
  },
  continueButton: {
    $base: {
      width: '100%',
      height: 48,
    },
  },
});
